import React, { useRef, useState } from 'react';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import { fileAPI } from '../../services/api/fileApi';

interface LogoUploaderProps {
  value?: string; // cover API path for preview (stored in KB metadata as cover_url)
  onChange: (coverUrl: string | null) => void;
  disabled?: boolean;
}

const LogoUploader: React.FC<LogoUploaderProps> = ({ value, onChange, disabled }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Validate file type
  const isValidFileType = (file: File): boolean => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    return validTypes.includes(file.type);
  };

  // Validate file size (max 2MB)
  const isValidFileSize = (file: File): boolean => {
    const maxSize = 2 * 1024 * 1024; // 2MB
    return file.size <= maxSize;
  };

  const handleFileSelect = async (file: File) => {
    setError(null);

    // Validate file type
    if (!isValidFileType(file)) {
      setError('Invalid file type. Please upload .jpg, .jpeg, .png, .gif, or .webp');
      return;
    }

    // Validate file size
    if (!isValidFileSize(file)) {
      setError('File size must be less than 2MB');
      return;
    }

    setUploading(true);

    try {
      // Use the new cover upload API
      const response = await fileAPI.uploadCover(file);

      // Store the API path (e.g., "/api/v1/knowledge-bases/covers/1_xxx.jpeg")
      const coverUrl = response.data.url;

      onChange(coverUrl);
      setError(null);
    } catch (err) {
      console.error('Cover upload failed:', err);
      setError('Failed to upload cover. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleClick = () => {
    if (!disabled && !uploading) {
      fileInputRef.current?.click();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
    // Reset input value to allow selecting the same file again
    e.target.value = '';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled && !uploading) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    if (disabled || uploading) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    setError(null);
  };

  return (
    <div className='flex flex-col items-center'>
      <div
        className={`
          relative w-48 h-20 rounded-lg flex items-center justify-center cursor-pointer
          transition-all duration-200 overflow-hidden
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:ring-2 hover:ring-blue-400'}
          ${isDragging ? 'ring-2 ring-blue-400 bg-blue-50' : 'bg-gray-100'}
          ${uploading ? 'cursor-wait' : ''}
          ${error ? 'ring-2 ring-red-400' : ''}
        `}
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {value ? (
          <>
            <img
              src={fileAPI.getCoverUrl(value)}
              alt='Cover preview'
              className='w-full h-full object-cover'
            />
            {!disabled && !uploading && (
              <button
                className='absolute top-0 right-0 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors'
                onClick={handleRemove}
                aria-label='Remove cover'
              >
                <X size={14} />
              </button>
            )}
          </>
        ) : (
          <div className='flex flex-col items-center justify-center text-gray-400'>
            {uploading ? (
              <div className='animate-spin rounded-full h-6 w-6 border-2 border-blue-500 border-t-transparent'></div>
            ) : (
              <>
                <ImageIcon size={24} />
                <Upload size={16} className='absolute bottom-1 right-1' />
              </>
            )}
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type='file'
        accept='.jpg,.jpeg,.png,.gif,.webp'
        onChange={handleInputChange}
        className='hidden'
        disabled={disabled || uploading}
      />

      {error && <p className='mt-2 text-xs text-red-500 text-center max-w-[200px]'>{error}</p>}

      {!value && !error && (
        <p className='mt-2 text-xs text-gray-500 text-center'>Click or drag to upload cover</p>
      )}

      {value && !error && (
        <p className='mt-2 text-xs text-gray-500 text-center'>Cover uploaded successfully</p>
      )}
    </div>
  );
};

export default LogoUploader;
